import type { QuickFillItem, QuickFillProperty, QuickFillRowMode } from '../../../shared/types';
import type {
  BulkUpdateResult,
  QuickFillCellUpdate,
  PropertyMapping,
} from '../../../shared/services/quickFillRevitService';
import type { QuickFillSessionUndoSnapshot } from '../../../shared/stores/useQuickFillStore';
import type { QuickFillSelectedCell } from './quickFillNavigationModel';
import { normalizeRevitParameterName } from '../../../shared/lib/revitParameterNames';

export * from './quickFillNavigationModel';
export * from './quickFillModelSelection';
export * from './quickFillOnboarding';

interface SaveReviewOutcome {
  warning?: string;
  result?: {
    failureCount?: number;
  } | null;
}

interface ValidationFocusColumnsReadyInput {
  validationFocusColumnKeys: Array<keyof QuickFillItem>;
  quickFillData: QuickFillItem[];
  isTableLoading: boolean;
}

interface ValidationFocusDatasetModeReadyInput {
  requestId: string | null | undefined;
  preferredRowMode?: QuickFillRowMode;
  quickFillData: QuickFillItem[];
  shouldUseFamilyTypeFallbackForValidation: boolean;
}

interface QuickFillCellFillDownPlanInput {
  selectedCells: QuickFillSelectedCell[];
  activeColumn: string | null;
  isValidSelection: boolean;
  rowCount: number;
}

interface QuickFillCellFillDownPlan {
  columnLabel: string;
  sourceRowIndex: number;
  targetRowIndices: number[];
  selectionCount: number;
}

export interface QuickFillCellPasteUpdate {
  rowIndex: number;
  field: string;
  value: string;
}

export interface QuickFillCellPastePlan {
  updates: QuickFillCellPasteUpdate[];
}

interface ConnectorParameterTargetLike {
  parameterDestination?: 'project' | 'family';
  parameterBindingKind?: 'type' | 'instance';
}

interface QuickFillParameterBindingGroup {
  parameterNames?: readonly string[];
  bindingKind?: 'type' | 'instance';
}

export type QuickFillConfiguredBindingKind = 'type' | 'instance' | 'mixed';

const normalizeQuickFillParameterBindingKey = (parameterName: string): string =>
  normalizeRevitParameterName(parameterName).trim().toLowerCase();

export const filterQuickFillPropertiesByAvailableParameters = (
  properties: QuickFillProperty[],
  availableParameters: ReadonlyArray<{ name?: string }>,
  availabilityLoaded: boolean
): QuickFillProperty[] => {
  if (!availabilityLoaded) {
    return properties;
  }

  const availableParameterKeys = new Set(
    availableParameters
      .map((parameter) => normalizeQuickFillParameterBindingKey(parameter?.name ?? ''))
      .filter(Boolean)
  );
  const filteredProperties = properties.filter((property) => {
    const key = normalizeQuickFillParameterBindingKey(property?.revitParameter ?? '');
    return Boolean(key) && availableParameterKeys.has(key);
  });

  return filteredProperties.length === properties.length ? properties : filteredProperties;
};

export const buildQuickFillParameterBindingKinds = (
  groups: readonly QuickFillParameterBindingGroup[]
): ReadonlyMap<string, QuickFillConfiguredBindingKind> => {
  const bindingKinds = new Map<string, QuickFillConfiguredBindingKind>();

  groups.forEach((group) => {
    const bindingKind = group.bindingKind;
    if (!bindingKind) {
      return;
    }

    group.parameterNames?.forEach((parameterName) => {
      const key = normalizeQuickFillParameterBindingKey(parameterName);
      if (!key) {
        return;
      }

      const existing = bindingKinds.get(key);
      if (existing && existing !== bindingKind) {
        bindingKinds.set(key, 'mixed');
        return;
      }

      bindingKinds.set(key, bindingKind);
    });
  });

  return bindingKinds;
};

export const resolveQuickFillPropertyBindingScope = (
  property: Pick<QuickFillProperty, 'revitParameter' | 'isInstance'>,
  bindingKinds?: ReadonlyMap<string, QuickFillConfiguredBindingKind>
): 'type' | 'instance' | undefined => {
  const key = normalizeQuickFillParameterBindingKey(property.revitParameter ?? '');
  const configuredBindingKind = key ? bindingKinds?.get(key) : undefined;
  if (configuredBindingKind === 'type' || configuredBindingKind === 'instance') {
    return configuredBindingKind;
  }
  if (configuredBindingKind === 'mixed') {
    return undefined;
  }

  return typeof property.isInstance === 'boolean'
    ? property.isInstance ? 'instance' : 'type'
    : undefined;
};

export const applyQuickFillParameterBindingKinds = (
  properties: QuickFillProperty[],
  bindingKinds: ReadonlyMap<string, QuickFillConfiguredBindingKind>
): QuickFillProperty[] => {
  let changed = false;
  const configuredProperties = properties.map((property) => {
    const key = normalizeQuickFillParameterBindingKey(property.revitParameter ?? '');
    const bindingKind = key ? bindingKinds.get(key) : undefined;
    if (bindingKind !== 'type' && bindingKind !== 'instance') {
      return property;
    }

    const isInstance = bindingKind === 'instance';
    if (property.isInstance === isInstance) {
      return property;
    }

    changed = true;
    return { ...property, isInstance };
  });

  return changed ? configuredProperties : properties;
};

export const resolveQuickFillConnectorDefaultRowMode = (
  connectorTarget: ConnectorParameterTargetLike | null | undefined
): QuickFillRowMode =>
  connectorTarget?.parameterDestination === 'project' &&
  connectorTarget.parameterBindingKind === 'instance'
    ? 'instance'
    : 'familyType';

export const resolveQuickFillSnapshotShape = (
  connectorTarget: ConnectorParameterTargetLike | null | undefined,
  rowMode: QuickFillRowMode
): 'familyType' | 'instanceOnly' =>
  resolveQuickFillConnectorDefaultRowMode(connectorTarget) === 'instance' && rowMode === 'instance'
    ? 'instanceOnly'
    : 'familyType';

export const resolveQuickFillNameColumnLabel = (rowMode: QuickFillRowMode): string =>
  rowMode === 'instance' ? 'Element' : 'Family / Type';

export const buildFailedCellErrors = (
  result: BulkUpdateResult | null,
  quickFillData: QuickFillItem[],
  parameterToLabel: Map<string, string>
): Record<string, string> => {
  const nextFailedCells: Record<string, string> = {};

  if (!result) {
    return nextFailedCells;
  }

  result.results
    .filter((row) => !row.success)
    .forEach((row) => {
      const label = parameterToLabel.get(row.parameter) || row.parameter;
      const errorText = row.error || 'Failed to apply value';

      quickFillData.forEach((item, idx) => {
        if (!item) {
          return;
        }
        if (item.Name === row.familyType) {
          nextFailedCells[`${idx}-${label}`] = errorText;
        }
      });
    });

  return nextFailedCells;
};

export const shouldPauseContinueForSaveReview = (outcome: SaveReviewOutcome): boolean =>
  Boolean(outcome.warning) || Boolean(outcome.result && outcome.result.failureCount && outcome.result.failureCount > 0);

export const cloneQuickFillRows = (data: QuickFillItem[]): QuickFillItem[] =>
  data.map((item) => ({ ...item }));

export const buildRollbackPropertyMappings = (
  snapshot: QuickFillSessionUndoSnapshot,
  currentProperties: QuickFillProperty[],
  currentExtraProperties: QuickFillProperty[]
): PropertyMapping[] => {
  const mappings = new Map<string, PropertyMapping>();
  const addProperty = (property: QuickFillProperty | undefined | null) => {
    if (!property?.label || property.label === 'Name' || !property.revitParameter) {
      return;
    }

    const key = `${property.label}||${property.revitParameter}`;
    if (!mappings.has(key)) {
      const bindingScope = resolveQuickFillPropertyBindingScope(property);
      mappings.set(key, {
        label: property.label,
        revitParameter: property.revitParameter,
        ...(bindingScope ? { bindingScope } : {}),
      });
    }
  };

  currentProperties.forEach(addProperty);
  currentExtraProperties.forEach(addProperty);
  snapshot.properties.forEach(addProperty);
  snapshot.extraProperties.forEach(addProperty);

  return Array.from(mappings.values());
};

export const buildParameterToLabelMap = (mappings: PropertyMapping[]): Map<string, string> => {
  const map = new Map<string, string>();
  mappings.forEach((mapping) => {
    map.set(mapping.revitParameter, mapping.label);
  });
  return map;
};

export const buildEditedQuickFillCellUpdates = (
  editedCells: Record<string, boolean>,
  quickFillData: QuickFillItem[],
  propertyMappings: PropertyMapping[]
): QuickFillCellUpdate[] => {
  const mappingByLabel = new Map<string, PropertyMapping>();
  propertyMappings.forEach((mapping) => {
    if (mapping.label && mapping.revitParameter) {
      mappingByLabel.set(mapping.label, mapping);
    }
  });

  return Object.entries(editedCells)
    .filter(([, edited]) => edited)
    .map(([cellKey]) => {
      const match = /^(\d+)-(.+)$/.exec(cellKey);
      if (!match) {
        return null;
      }

      const rowIndex = Number.parseInt(match[1], 10);
      const field = match[2];
      const row = quickFillData[rowIndex];
      const mapping = mappingByLabel.get(field);
      if (!row || !mapping) {
        return null;
      }

      const value = row[field];
      const normalizedValue = value == null || String(value).trim() === '' ? '' : String(value);
      const rowBindingScope = (
        ((row as any).__bindingScopeFlags as Record<string, unknown> | undefined) ?? {}
      )[field];
      const hasRowBindingScope = rowBindingScope === 'type' || rowBindingScope === 'instance';
      // Instance rows are resolved from a concrete element and can legitimately
      // differ for the same parameter name. Family/type rows have no such
      // element identity, so their active setup corrects stale snapshot flags.
      const bindingScope = (row as any).__rowMode === 'instance' && hasRowBindingScope
        ? rowBindingScope
        : mapping.bindingScope ?? (hasRowBindingScope ? rowBindingScope : undefined);

      return {
        rowIndex,
        field,
        value: normalizedValue,
        revitParameter: mapping.revitParameter,
        ...(bindingScope ? { bindingScope } : {}),
      };
    })
    .filter((update): update is QuickFillCellUpdate => update !== null)
    .sort((left, right) => (
      left.rowIndex === right.rowIndex
        ? left.field.localeCompare(right.field)
        : left.rowIndex - right.rowIndex
    ));
};

export const buildQuickFillCellFillDownPlan = ({
  selectedCells,
  activeColumn,
  isValidSelection,
  rowCount,
}: QuickFillCellFillDownPlanInput): QuickFillCellFillDownPlan | null => {
  const columnLabel = (activeColumn || '').trim();
  if (!columnLabel || !isValidSelection || rowCount <= 1) {
    return null;
  }

  const selectedRowIndices = Array.from(
    new Set(
      selectedCells
        .filter((cell) => cell.columnLabel === undefined || cell.columnLabel === columnLabel)
        .map((cell) => cell.rowIndex)
        .filter((rowIndex) => Number.isInteger(rowIndex) && rowIndex >= 0 && rowIndex < rowCount)
    )
  ).sort((left, right) => left - right);

  if (selectedRowIndices.length === 0) {
    return null;
  }

  const sourceRowIndex = selectedRowIndices[0];
  const targetRowIndices =
    selectedRowIndices.length > 1
      ? selectedRowIndices.slice(1)
      : Array.from(
          { length: Math.max(0, rowCount - sourceRowIndex - 1) },
          (_, index) => sourceRowIndex + index + 1
        );

  if (targetRowIndices.length === 0) {
    return null;
  }

  return {
    columnLabel,
    sourceRowIndex,
    targetRowIndices,
    selectionCount: selectedRowIndices.length > 1
      ? selectedRowIndices.length
      : targetRowIndices.length + 1,
  };
};

export const buildQuickFillCellPastePlan = (
  parsedItems: QuickFillItem[],
  targetCells: QuickFillSelectedCell[],
  rowCount: number = Number.MAX_SAFE_INTEGER
): QuickFillCellPastePlan | null => {
  const sourceRow = parsedItems[0];
  if (!sourceRow) {
    return null;
  }

  const sourceColumns = Object.keys(sourceRow).filter(
    (column) => column !== 'Name' && !column.startsWith('__')
  );
  if (sourceColumns.length !== 1) {
    return null;
  }

  const sourceColumn = sourceColumns[0];
  const validTargets = targetCells
    .filter((cell) => (
      Number.isInteger(cell.rowIndex) &&
      cell.rowIndex >= 0 &&
      cell.rowIndex < rowCount &&
      typeof cell.columnLabel === 'string' &&
      cell.columnLabel.trim() !== '' &&
      cell.columnLabel !== 'Name'
    ))
    .sort((left, right) => left.rowIndex - right.rowIndex);
  if (validTargets.length === 0) {
    return null;
  }

  return {
    updates: validTargets.map((target, index) => ({
      rowIndex: target.rowIndex,
      field: target.columnLabel ?? '',
      value: String(parsedItems[index % parsedItems.length]?.[sourceColumn] ?? ''),
    })),
  };
};

export const buildQuickFillParameterLabelMap = (
  properties: QuickFillProperty[],
  extraProperties: QuickFillProperty[]
): Map<string, string> => {
  const map = new Map<string, string>();
  [...properties, ...(Array.isArray(extraProperties) ? extraProperties : [])].forEach((prop) => {
    if (prop?.revitParameter && prop?.label) {
      map.set(prop.revitParameter, prop.label);
    }
  });
  return map;
};

export const getQuickFillDataRowModes = (quickFillData: QuickFillItem[]): QuickFillRowMode[] =>
  Array.from(
    new Set(
      quickFillData.map(
        (item) => (((item as any)?.__rowMode as QuickFillRowMode | undefined) ?? 'familyType')
      )
    )
  );

export const isQuickFillDatasetModeReady = (
  rowModes: readonly QuickFillRowMode[],
  expectedRowMode: QuickFillRowMode,
  isLoading: boolean
): boolean => !isLoading && rowModes.length === 1 && rowModes[0] === expectedRowMode;

export const areValidationFocusColumnsReadyInData = ({
  validationFocusColumnKeys,
  quickFillData,
  isTableLoading,
}: ValidationFocusColumnsReadyInput): boolean => {
  if (validationFocusColumnKeys.length === 0) {
    return true;
  }

  if (quickFillData.length === 0) {
    return !isTableLoading;
  }

  return validationFocusColumnKeys.every((columnKey) =>
    quickFillData.some((item) => Object.prototype.hasOwnProperty.call(item, columnKey))
  );
};

export const isValidationFocusDatasetModeReady = ({
  requestId,
  preferredRowMode,
  quickFillData,
  shouldUseFamilyTypeFallbackForValidation,
}: ValidationFocusDatasetModeReadyInput): boolean => {
  if (!requestId) {
    return true;
  }

  const expectedRowMode = shouldUseFamilyTypeFallbackForValidation
    ? 'familyType'
    : preferredRowMode ?? 'familyType';
  if (quickFillData.length === 0) {
    return false;
  }

  return quickFillData.every(
    (item) => ((((item as any)?.__rowMode as QuickFillRowMode | undefined) ?? 'familyType') === expectedRowMode)
  );
};
